import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Modal from 'react-modal';
import Select from 'react-select';
import axios from 'axios';
import { updateField } from '../../../../actions/BookingRequest';
import { ErrorMessage } from '../../../common';
import { ROOT_URL_V2 } from '../../../../config';
import { ModalConfirmEditSeriesQuestions } from '../../../operations/series/ConfirmEditQuestions';
import { jobModes } from '../../../../consts';
import { TPageNotification } from '../../../../types';


type TModalAddQualifyingQuestionsProps = {
    initialQualifyingQuestions: TQualifyingQuestion[];
    setQualifyingQuestions: (newQualifyingQuestions: TQualifyingQuestion[]) => void;
    handleClose: () => void;
    isOpen: boolean;
    seriesId?: number;
    isEditedBySeries?: boolean;
    notification?: any;
    reloadData?: () => void;
    mode?: string;
};

export function ModalAddQualifyingQuestions({
    initialQualifyingQuestions,
    setQualifyingQuestions,
    handleClose,
    isOpen,
    seriesId,
    isEditedBySeries,
    notification,
    reloadData,
    mode,
}: TModalAddQualifyingQuestionsProps) {
    const dispatch = useDispatch();
    const [error, setError] = useState(null);
    const initialFields = Array.from({ length: 3 }, (_, index) =>
        initialQualifyingQuestions && initialQualifyingQuestions[index]
            ? initialQualifyingQuestions[index]
            : { content: '', correct_answer: null }
    );

    const [fields, setFields] = useState(initialQualifyingQuestions);
    const [qualifyingQuestionsTemplate, setQualifyingQuestionsTemplate] = useState([]);
    const [isModalConfirmEditQuestionsOpen, setIsModalConfirmEditQuestionsOpen] = useState(false);

    const isEmpty = (fieldsRow: TQualifyingQuestion) =>
        (fieldsRow.content === null || fieldsRow.content === undefined || fieldsRow.content === '') &&
        (fieldsRow.correct_answer === null || fieldsRow.correct_answer === undefined);

    const isFilled = (fieldsRow: TQualifyingQuestion) =>
        (fieldsRow.content && fieldsRow.content.trim().length > 0) &&
        (fieldsRow.correct_answer !== null && fieldsRow.correct_answer !== undefined);

    const fieldsAreValid = () => {
        const selectedQuestions = Object.values(fields).filter(field => field.content).map(field => field.content);
        if (selectedQuestions.length !== new Set(selectedQuestions).size) {
            setError('Questions cannot be duplicated.');
            return false;
        }
        if (Object.values(fields).some(field => field.content && field.content.length > 100)) {
            setError('Question cannot be longer than 100 characters.');
            return false;
        }
        return true;
    };

    const isFormValid = () => {
        const invalidFields = Object.values(fields).filter(field => (!isEmpty(field) && !isFilled(field)));

        if (invalidFields.length) {
            setError('All fields in row must be either empty or filled.');
            return false;
        }
        return true;
    };

    useEffect(() => {
        const getQualifyingQuestionsTemplate = async() => {
            try {
                const response = await axios({
                    method: 'GET',
                    url: `${ROOT_URL_V2}qualifying-questions-templates/`,
                });

                setQualifyingQuestionsTemplate(response.data?.content);
            } catch (er) {
                // eslint-disable-next-line no-console
                console.log(er);
            }
        };
        getQualifyingQuestionsTemplate();
    }, []);

    useEffect(() => {
        if (isEditedBySeries) {
            const getCurrentQualifyingQuestions = async() => {
                try {
                    const response = await axios({
                        method: 'GET',
                        url: `${ROOT_URL_V2}series/${seriesId}/qualifying-questions/`,
                    });
                    setFields(response.data?.content.map((question: TQualifyingQuestion) => ({
                        content: question.content,
                        correct_answer: question.correct_answer,
                    })));
                } catch (er) {
                    // eslint-disable-next-line no-console
                    console.log(er);
                }
            };
            getCurrentQualifyingQuestions();
        }
    }, [isEditedBySeries, seriesId]);

    const handleFieldChange = (value: string, index: number, fieldName: string) => {
        setFields(prevFields => ({
            ...prevFields,
            [index]: {
                ...prevFields[index],
                [fieldName]: value
            }
        }));
    };

    const handleClearFieldsRow = (event: any, index: number) => {
        event.preventDefault();
        // @ts-ignore
        setFields(prevFields => ({
            ...prevFields,
            [index]: {
                content: '',
                correct_answer: null,
            }
        }));
    };

    const handleOpenModalConfirmEditQuestions = (): void => {
        setIsModalConfirmEditQuestionsOpen(true);
    };

    const handleCloseModalConfirmEditQuestions = (): void => {
        setIsModalConfirmEditQuestionsOpen(false);
    };

    const handleSave = () => {
        if (isFormValid() && fieldsAreValid()) {
            if (!isEditedBySeries) {
                const newQualifyingQuestions = Object.values(fields)
                    .filter(field => field.content !== null && field.content !== '' && field.correct_answer !== null);
                setQualifyingQuestions(newQualifyingQuestions);

                dispatch(updateField('qualifying_questions', newQualifyingQuestions));
                handleClose();
            } else {
                setError(null);
                handleOpenModalConfirmEditQuestions();
            }

        }
    };

    return (
            <div>
                <Modal
                    className="add-qualifying-questions-modal"
                    isOpen={isOpen}
                    onRequestClose={handleClose}
                    contentLabel="Qualifying questions"
                    ariaHideApp={false}
                >
                <div className="popup-header">
                    {isEditedBySeries || mode === jobModes.EDIT ? (
                        <h1>
                            Edit Qualifying Questions
                        </h1>
                    ) : (
                        <h1>
                            Add Qualifying Questions
                        </h1>
                    )}
                </div>
                    <div className="cancel-worker-body">
                        <p>
                            This option is exclusive to auto-matched jobs and auto-matched ongoing jobs.<br/>
                            It allows you to add <strong>up to 3</strong> qualifying questions
                            to help find the best candidates for the job.<br/>
                            Please note, your questions should be formatted as <strong>Yes/No</strong> questions.
                        </p>
                    </div>
                    <form className="qualifying-questions-form">
                        <table>
                            <thead>
                            <tr>
                                <th>Question</th>
                                <th>Correct answer</th>
                            </tr>
                            </thead>
                            <tbody>
                            {Object.keys(initialFields).map((key: any) => (
                                <tr key={key}>
                                    <td className="qualifying-questions-field">
                                        <input
                                            className="Select qualifying-questions-content-input"
                                            type="text"
                                            value={fields[key]?.content}
                                            onChange={event => {
                                                handleFieldChange(event?.target?.value, key, 'content');
                                            }}
                                            placeholder="Select or write qualifying question..."
                                            list="qualifying-questions"
                                        />
                                        <datalist
                                            id="qualifying-questions"
                                            className="qualifying-questions-options"
                                        >
                                            {qualifyingQuestionsTemplate.map(question => (
                                                <option key={question.id} value={question.content} />
                                            ))}
                                        </datalist>
                                    </td>
                                    <td>
                                        <Select
                                            name="correct_answer"
                                            className="qualifying-questions-select"
                                            // @ts-ignore
                                            value={fields[key]?.correct_answer}
                                            options={[
                                                { value: true, label: 'Yes' },
                                                { value: false, label: 'No' }
                                            ]}
                                            onChange={event => {
                                                handleFieldChange(event?.value, key, 'correct_answer');
                                            }}
                                            isClearable={true}
                                            placeholder="Select..."
                                        />
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            onClick={event => {
                                                handleClearFieldsRow(event, key);
                                            }}
                                        >
                                            <i
                                                className="icon icon-remove-circle-1 red"
                                            />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        <button
                            className="continue btn--round-big"
                            onClick={handleSave}
                            type="button"
                        >
                            SAVE
                        </button>
                        <button
                            className="back"
                            onClick={handleClose}
                            type="button"
                        >
                            CANCEL
                        </button>
                    </form>
                    {error ? (<ErrorMessage message={error}/>) : null}
                    {isModalConfirmEditQuestionsOpen &&
                        <ModalConfirmEditSeriesQuestions
                            notification={notification}
                            reloadData={reloadData}
                            isOpen={isModalConfirmEditQuestionsOpen}
                            handleClose={handleCloseModalConfirmEditQuestions}
                            handleCloseParentModal={handleClose}
                            fields={fields}
                            seriesId={seriesId}
                            setError={setError}
                        />
                    }
                </Modal>
            </div>
    );
}

export type TQualifyingQuestion = {
    id?: number;
    content: string;
    correct_answer: boolean;
};

type TQualifyingQuestionsProps = {
    initialQualifyingQuestions: TQualifyingQuestion[];
    setQualifyingQuestions: (newQualifyingQuestions: TQualifyingQuestion[]) => void;
    mode: string;
};

export default function QualifyingQuestions({
    initialQualifyingQuestions,
    setQualifyingQuestions,
    mode,
}: TQualifyingQuestionsProps) {
    const [isModalAddQualifyingQuestionsOpen, setIsModalAddQualifyingQuestionsOpen] = useState(false);

    const handleOpenAddQualifyingQuestionsModal = () => {
        setIsModalAddQualifyingQuestionsOpen(true);
    };

    const handleCloseAddQualifyingQuestionsModal = () => {
        setIsModalAddQualifyingQuestionsOpen(false);
    };

    return (
        <div>
            <a
                onClick={handleOpenAddQualifyingQuestionsModal}
                className="add-qualifying-questions-link"
            >
                {initialQualifyingQuestions?.length > 0
                    ? 'Edit Qualifying Questions...'
                    : 'Add Qualifying Questions...'
                }
            </a>
            {isModalAddQualifyingQuestionsOpen && (
                <ModalAddQualifyingQuestions
                    initialQualifyingQuestions={initialQualifyingQuestions}
                    setQualifyingQuestions={setQualifyingQuestions}
                    handleClose={handleCloseAddQualifyingQuestionsModal}
                    isOpen={isModalAddQualifyingQuestionsOpen}
                    mode={mode}
                />
            )}
        </div>
    );
}

type TModalSeriesQualifyingQuestions = {
    notification: TPageNotification;
    reloadData: () => void;
    handleClose: (modalName: string) => void;
    isOpen: boolean;
    seriesId: number;
};

export function ModalSeriesQualifyingQuestions({
    notification,
    reloadData,
    handleClose,
    isOpen,
    seriesId,
}: TModalSeriesQualifyingQuestions) {

    const [qualifyingQuestions, setQualifyingQuestions] = useState([]);

    const handleCloseAddQualifyingQuestionsModal = () => {
        handleClose('SeriesQualifyingQuestions');
    };

    return (
        <div>
            {isOpen && (
                <ModalAddQualifyingQuestions
                    initialQualifyingQuestions={qualifyingQuestions}
                    setQualifyingQuestions={setQualifyingQuestions}
                    handleClose={handleCloseAddQualifyingQuestionsModal}
                    isOpen={isOpen}
                    seriesId={seriesId}
                    isEditedBySeries={true}
                    notification={notification}
                    reloadData={reloadData}
                />
            )}
        </div>
    );
}
